// ============================================================================
// THE CYCLE — Expected Stats Card (xBA / xSLG)
// ============================================================================
// Displays expected batting stats based on exit velocity and launch angle,
// barrel rate, sweet spot %, hard hit %, and EV/LA distributions.

import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, CircularProgress, Chip, useTheme, alpha,
  Grid, LinearProgress, Tooltip,
} from '@mui/material';
import { pitchSplitsApi } from '../services/apiService';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const toPercentile = (value, min, max, higherIsBetter = true) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const normalized = ((Number(value) - min) / (max - min)) * 100;
  const bounded = clamp(normalized, 0, 100);
  return higherIsBetter ? bounded : 100 - bounded;
};

const PercentileBar = ({ label, value, percentile, theme }) => {
  if (percentile == null) return null;

  const fillPct = clamp(percentile, 0, 100);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 88 }}>
        {label}
      </Typography>
      <Box sx={{
        position: 'relative',
        flex: 1,
        height: 11,
        borderRadius: 999,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
        background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.error.main, 0.9)} 100%)`,
      }}>
        <Box sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: `${100 - fillPct}%`,
          bgcolor: alpha(theme.palette.background.paper, 0.86),
          transition: 'width 0.3s ease',
        }} />
      </Box>
      <Typography variant="caption" fontWeight={800} sx={{ minWidth: 72, textAlign: 'right' }}>
        {Math.round(fillPct)}th
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 62, textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
};

const ExpectedStatsCard = ({ team, playerName, season, perspective = 'batter', gameType }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!team || !playerName) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await pitchSplitsApi.getExpectedStats(perspective, team, playerName, season, { gameType });
        setData(res);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [team, playerName, season, perspective, gameType]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Typography color="error" variant="body2">{error}</Typography>;
  if (!data) return null;

  const {
    xBA, xSLG, barrelPct, sweetSpotPct, hardHitPct,
    avgEV, avgLA, battedBalls, actualBA,
    evDistribution, laDistribution,
  } = data;

  const isPitcherView = perspective === 'pitcher';

  const expectedVsActualDelta =
    actualBA != null && xBA != null ? Number((actualBA - xBA).toFixed(3)) : null;

  const percentileRows = [
    {
      label: 'xBA',
      rawValue: xBA,
      displayValue: xBA != null ? xBA.toFixed(3) : '—',
      percentile: toPercentile(xBA, 0.18, 0.33, !isPitcherView),
    },
    {
      label: 'xSLG',
      rawValue: xSLG,
      displayValue: xSLG != null ? xSLG.toFixed(3) : '—',
      percentile: toPercentile(xSLG, 0.28, 0.65, !isPitcherView),
    },
    {
      label: 'Barrel%',
      rawValue: barrelPct,
      displayValue: barrelPct != null ? `${barrelPct.toFixed(1)}%` : '—',
      percentile: toPercentile(barrelPct, 2, 18, !isPitcherView),
    },
    {
      label: 'HardHit%',
      rawValue: hardHitPct,
      displayValue: hardHitPct != null ? `${hardHitPct.toFixed(1)}%` : '—',
      percentile: toPercentile(hardHitPct, 25, 55, !isPitcherView),
    },
    {
      label: 'Avg EV',
      rawValue: avgEV,
      displayValue: avgEV != null ? `${avgEV.toFixed(1)} mph` : '—',
      percentile: toPercentile(avgEV, 84, 95, !isPitcherView),
    },
  ].filter((row) => row.rawValue != null);

  // Header stat boxes
  const headerStats = [
    { label: 'xBA', value: xBA?.toFixed(3), highlight: true, color: theme.palette.error.main },
    { label: 'xSLG', value: xSLG?.toFixed(3), highlight: true, color: theme.palette.primary.main },
    { label: 'Actual BA', value: actualBA?.toFixed(3), highlight: true, color: theme.palette.success.main },
    { label: 'Avg EV', value: avgEV ? `${avgEV.toFixed(1)}` : '—', suffix: ' mph', highlight: avgEV >= 90 },
    { label: 'Avg LA', value: avgLA ? `${avgLA.toFixed(1)}°` : '—' },
    { label: 'Batted Balls', value: battedBalls || 0 },
  ];

  // Quality of contact metrics — thresholds differ by perspective
  // For pitchers, LOWER values are better (opponents' contact quality)
  const qualityMetrics = isPitcherView
    ? [
        { label: 'Barrel%',    value: barrelPct,    lowerGood: true,  goodThreshold: 6,  eliteThreshold: 4,  description: 'Opponent barrel rate (lower = better)' },
        { label: 'Sweet Spot%',value: sweetSpotPct, lowerGood: true,  goodThreshold: 35, eliteThreshold: 28, description: 'Opponent sweet spot rate (lower = better)' },
        { label: 'Hard Hit%',  value: hardHitPct,   lowerGood: true,  goodThreshold: 38, eliteThreshold: 32, description: 'Opponent hard hit rate (lower = better)' },
      ]
    : [
        { label: 'Barrel%',    value: barrelPct,    lowerGood: false, goodThreshold: 8,  eliteThreshold: 12, description: 'Exit velo ≥98 + ideal launch angle' },
        { label: 'Sweet Spot%',value: sweetSpotPct, lowerGood: false, goodThreshold: 30, eliteThreshold: 40, description: 'Launch angle 8-32°' },
        { label: 'Hard Hit%',  value: hardHitPct,   lowerGood: false, goodThreshold: 40, eliteThreshold: 50, description: 'Exit velocity ≥95 mph' },
      ];

  // EV distribution buckets
  const evBuckets = evDistribution ? [
    { label: '<80', count: evDistribution.soft || 0, color: '#e74c3c' },
    { label: '80-95', count: evDistribution.medium || 0, color: '#f39c12' },
    { label: '95-105', count: evDistribution.hard || 0, color: '#2ecc71' },
    { label: '105+', count: evDistribution.barrel || 0, color: '#1abc9c' },
  ] : [];

  const totalEV = evBuckets.reduce((s, b) => s + b.count, 0);

  // LA distribution buckets
  const laBuckets = laDistribution ? [
    { label: 'Ground Ball', count: laDistribution.groundBall || 0, color: '#e67e22', type: 'LA < 10°' },
    { label: 'Line Drive', count: laDistribution.lineDrive || 0, color: '#2ecc71', type: 'LA 10-25°' },
    { label: 'Fly Ball', count: laDistribution.flyBall || 0, color: '#3498db', type: 'LA 25-50°' },
    { label: 'Pop Up', count: laDistribution.popup || 0, color: '#9b59b6', type: 'LA 50+°' },
  ] : [];

  const totalLA = laBuckets.reduce((s, b) => s + b.count, 0);

  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={800}>Expected Stats</Typography>
        <Chip label="Statcast" size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
      </Box>

      {/* Header Stats */}
      <Grid container spacing={1} sx={{ mb: 2.5 }}>
        {headerStats.map(stat => (
          <Grid item xs={6} sm key={stat.label}>
            <Box sx={{
              p: 1.5, borderRadius: 1.5, textAlign: 'center',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              bgcolor: stat.highlight ? alpha(stat.color || theme.palette.primary.main, 0.04) : 'transparent',
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>
                {stat.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: stat.highlight ? '1.2rem' : '1rem' }}>
                {stat.value ?? '—'}{stat.suffix || ''}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {expectedVsActualDelta != null && (
        <Box sx={{
          mb: 2.5,
          px: 1.25,
          py: 0.85,
          borderRadius: 1.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          bgcolor: alpha(expectedVsActualDelta >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.07),
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {`Actual BA vs xBA (${isPitcherView ? 'opponents' : 'player'})`}
          </Typography>
          <Typography variant="body2" fontWeight={800} sx={{
            color: expectedVsActualDelta >= 0 ? 'success.main' : 'error.main',
          }}>
            {`${expectedVsActualDelta >= 0 ? '+' : ''}${expectedVsActualDelta.toFixed(3)}`}
          </Typography>
        </Box>
      )}

      {percentileRows.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Statcast Percentiles
          </Typography>
          <Box sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
            borderRadius: 1.5,
            px: 1,
            py: 1,
            bgcolor: alpha(theme.palette.background.default, 0.35),
          }}>
            {percentileRows.map((row) => (
              <PercentileBar
                key={row.label}
                label={row.label}
                value={row.displayValue}
                percentile={row.percentile}
                theme={theme}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Quality of Contact */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Quality of Contact</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
        {qualityMetrics.map(m => {
          const v = m.value || 0;
          const isElite = m.lowerGood ? v <= m.eliteThreshold : v >= m.eliteThreshold;
          const isGood  = m.lowerGood ? v <= m.goodThreshold  : v >= m.goodThreshold;
          const barColor = isElite
            ? theme.palette.success.main
            : isGood
              ? theme.palette.warning.main
              : theme.palette.error.main;
          // Bar fill: for lower-is-better, invert progress so elite (low) = full bar
          const barValue = m.lowerGood
            ? Math.min(Math.max((1 - v / 60) * 100, 0), 100)
            : Math.min((v / 60) * 100, 100);
          return (
          <Tooltip key={m.label} title={m.description} arrow placement="left">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" fontWeight={700} sx={{ minWidth: 80 }}>{m.label}</Typography>
              <LinearProgress
                variant="determinate"
                value={barValue}
                sx={{
                  flex: 1, height: 10, borderRadius: 1,
                  bgcolor: alpha(theme.palette.divider, 0.15),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: barColor,
                    borderRadius: 1,
                  },
                }}
              />
              <Typography variant="body2" fontWeight={800} sx={{
                minWidth: 45, textAlign: 'right',
                color: isElite ? 'success.main' : isGood ? 'warning.main' : 'error.main',
              }}>
                {v?.toFixed(1) ?? '—'}%
              </Typography>
            </Box>
          </Tooltip>
          );
        })}
      </Box>

      {/* EV Distribution */}
      {totalEV > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Exit Velocity Distribution</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, height: 60, alignItems: 'flex-end' }}>
            {evBuckets.map(b => {
              const pct = totalEV > 0 ? (b.count / totalEV) * 100 : 0;
              return (
                <Tooltip key={b.label} title={`${b.label} mph: ${b.count} (${pct.toFixed(1)}%)`} arrow>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      width: '100%', borderRadius: '4px 4px 0 0',
                      bgcolor: alpha(b.color, 0.7),
                      height: `${Math.max(pct * 1.2, 3)}px`,
                      transition: 'height 0.3s',
                    }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', mt: 0.25 }}>{b.label}</Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* LA Distribution */}
      {totalLA > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Launch Angle Distribution</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, height: 60, alignItems: 'flex-end' }}>
            {laBuckets.map(b => {
              const pct = totalLA > 0 ? (b.count / totalLA) * 100 : 0;
              return (
                <Tooltip key={b.label} title={`${b.label}: ${b.count} (${pct.toFixed(1)}%) — ${b.type}`} arrow>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      width: '100%', borderRadius: '4px 4px 0 0',
                      bgcolor: alpha(b.color, 0.7),
                      height: `${Math.max(pct * 1.2, 3)}px`,
                      transition: 'height 0.3s',
                    }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', mt: 0.25 }}>{b.label}</Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default ExpectedStatsCard;
